/*
 * Shopify Forms app embed — inject the branded button font into its shadow DOM.
 *
 * The app (<shopify-forms-embed>) renders in an OPEN shadow DOM and exposes most
 * of its look as :host CSS custom properties (handled declaratively in
 * snippets/shopify-forms-theme.liquid). Two things it does NOT expose need a
 * shadow-injected <style>, both referencing custom properties set on the host in
 * Liquid (custom properties cross the shadow boundary):
 *   1. the submit button's font-family (stable [data-testid="btn-form-submit"]);
 *   2. the fixed teaser/popup's bottom offset, so it can be lifted above the
 *      sticky add-to-cart bar (stable `.appEmbed` root class — the hashed
 *      `_appEmbed…` variants churn on app updates, the plain one does not).
 * No hashed class names, so it survives app updates.
 */
(function () {
  var RULE =
    // Branded button font (Modesto).
    '[data-testid="btn-form-submit"]{' +
    'font-family:var(--forms-button-font,inherit)!important;' +
    'font-weight:var(--forms-button-font-weight,400)!important;' +
    'letter-spacing:normal!important;text-transform:none!important;}' +
    // Min 16px on the fields so iOS Safari doesn't zoom in on focus.
    'input,textarea,select{font-size:16px!important;}' +
    // More room at the top of the fields: pushes the typed text down so the
    // floated (shrunk) label clears it. Value comes from the host's
    // --forms-field-padding-top (set in Liquid). height:auto lets the box
    // grow with the padding instead of clipping the text if the app set a
    // fixed field height (textarea keeps its own height — the app sizes it).
    // Checkboxes/radios excluded: padding would distort them.
    'input:not([type="checkbox"]):not([type="radio"]),select{' +
    'padding-top:var(--forms-field-padding-top,24px)!important;height:auto!important;}' +
    'textarea{padding-top:var(--forms-field-padding-top,24px)!important;}' +
    // Fill the column without clipping: the app centres a capped-width inline
    // form and reserves an (empty) image column, so it floats narrow. Force the
    // form container full-width + single-column and drop the empty image cell.
    // box-sizing:border-box is what stops the right-edge clip — the app's
    // containers use content-box + padding, so a plain width:100% overflowed.
    // Targets the app's semantic class prefixes (stable across hash suffixes).
    '#app-embed,form,[class*="container"],[class*="inline"],[class*="grid"]{' +
    'width:100%!important;max-width:100%!important;box-sizing:border-box!important;}' +
    // At narrow breakpoints the app turns _container / _inline / _grid into
    // grids that CENTRE a fixed-width column (grid-template-columns:<fixed> +
    // justify-content/items:center) — the actual cause of the narrow inline form.
    // Force a single full-width track, stretched. (On non-grids these are inert.)
    // Excludes the POPUP subtree (`:not(:has(close button))`) so the app's own
    // image-right 2-column popup layout is left intact — only inline embeds are
    // collapsed to one column.
    '[class*="container"]:not(:has([class*="CloseButton"])),' +
    '[class*="inline"]:not(:has([class*="CloseButton"])),' +
    '[class*="grid"]:not(:has([class*="CloseButton"])){' +
    'grid-template-columns:1fr!important;justify-content:stretch!important;justify-items:stretch!important;}' +
    '[class*="imageLoading"]{display:none!important;}' +
    // Right-edge clip fix for the fields themselves: the fill rules above only
    // border-box the lowercase container/inline/grid classes, but the app's
    // FIELD wrappers are camelCase (_fieldContainer_, _textField_, …) and the
    // inputs size as content-box — width:100% + their own horizontal padding
    // overflows the form column by the padding amount, and the overflow is
    // clipped on the right. border-box + max-width caps every field at its
    // container. Deliberately NO width:100% here, so narrow controls (the
    // phone country-code select) keep their own width.
    'input,textarea,select,[class*="field"],[class*="Field"]{' +
    'max-width:100%!important;box-sizing:border-box!important;}' +
    // …the remaining clipped rows trace to the form FIELDSET's own padding
    // (live-verified in devtools: repainting form._formFieldset_… with
    // `padding: 0 3px` fixes every still-clipped row; the File Upload row had
    // already been caught by the "Field" rule above). Pin that padding via
    // the stable semantic prefix — the hashed suffix churns on app updates.
    'form[class*="formFieldset"]{padding:0 3px!important;}' +
    // Lead-capture POPUP (the "10% off" teaser) renders INSIDE this same shadow
    // root, so a theme {% style %} block (light DOM) can't reach it. Left alone —
    // and with the fill rules above also matching its `_formContainer` — the card
    // fills the screen. Cap + centre it with a gutter. The ONLY reliable popup
    // marker is the close (X) button: a popup card contains a
    // `_formContainerCloseButtonPosition` / `_formCloseButton` (both include the
    // substring "CloseButton"), an embedded form NEVER does — so `:has(close
    // button)` caps popups only and can't touch an inline embed (the app wraps
    // both in the same `_fadeAnimation`/`_container` shells, so ancestor-class
    // scoping wrongly hit embeds too). `:has()` contributes its arg's
    // specificity, so this is (0,2,0), beating the fill rules above (0,1,0).
    // Cap + centre the popup card so it isn't full-bleed, but leave its layout
    // ALONE — the merchant's "image right" 2-column design renders natively now
    // that the fill rule above no longer collapses it. `width:fit-content` makes
    // the card hug its content (form + image); the app's columns are
    // content-sized justified to the start, so a stretched `width:100%` left dead
    // white space to the right of the image. max-width is a ceiling only;
    // `calc(100% - 32px)` keeps the mobile gutter. `width:fit-content` (0,2,0)
    // also overrides the width-fill rule's `width:100%` (0,1,0) on the popup.
    '[data-sizing="form-wrapper"]:has([class*="CloseButton"]),' +
    '[class*="formContainer"]:has([class*="CloseButton"]){' +
    'max-width:min(1000px,calc(100% - 32px))!important;' +
    'width:fit-content!important;margin-inline:auto!important;box-sizing:border-box!important;}' +
    // Lift the fixed teaser above the sticky add-to-cart bar. Reads the
    // --forms-teaser-offset custom property set on the host in Liquid (0 normally,
    // the bar's height when it's stuck); custom properties cross the shadow boundary.
    '.appEmbed{bottom:var(--forms-teaser-offset,0px)!important;' +
    'transition:bottom .3s ease!important;}' +
    // Inset the collapsed teaser off the viewport's left/right edges so the pill
    // isn't flush against the corner. Reads --forms-teaser-gutter from the host
    // (set in Liquid); scoped to the teaser (class contains "Teaser") so the
    // expanded form/popup's own layout is untouched. The teaser root is a
    // full-viewport-width fixed strip (left:0; right:0), so insetting both edges
    // moves the left-anchored pill in from the corner.
    '.appEmbed[class*="Teaser"]{' +
    'left:var(--forms-teaser-gutter,1rem)!important;' +
    'right:var(--forms-teaser-gutter,1rem)!important;}' +
    // The collapsed teaser root is a full-viewport-width fixed strip
    // (position:fixed; left:0; right:0), so its transparent area around the small
    // visible pill swallowed clicks on the footer/links beneath it. Making only
    // the root click-through is NOT enough: the app re-enables
    // pointer-events:auto on its own full-width inner wrapper
    // ([data-testid="app-router"] / ._container_), which overrides the root's
    // inherited `none` and keeps swallowing clicks across the whole width. So
    // force the ENTIRE teaser subtree click-through (root + every descendant,
    // neutralising the app's wrapper regardless of its hashed class), then
    // re-enable pointer-events only on the interactive LEAVES (pill button +
    // close). The leaf selectors outrank the `*` rule on specificity; the
    // pill/close's inner text + icons hit-test through to those auto leaves.
    // Scoped to the teaser (class contains "Teaser"), so the expanded
    // form/popup is untouched.
    '.appEmbed[class*="Teaser"],.appEmbed[class*="Teaser"] *{pointer-events:none!important;}' +
    '.appEmbed[class*="Teaser"] a,.appEmbed[class*="Teaser"] button,' +
    '.appEmbed[class*="Teaser"] input,.appEmbed[class*="Teaser"] [role="button"],' +
    '.appEmbed[class*="Teaser"] [tabindex]{pointer-events:auto!important;}';

  function inject(root) {
    if (!root || root.querySelector('style[data-forms-theme]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-forms-theme', '');
    style.textContent = RULE;
    root.appendChild(style);
  }

  // Mark ONLY the fixed floating teaser/popup host with [data-forms-floating].
  // The app renders the teaser with a `.appEmbed` root inside its shadow tree;
  // an INLINE embedded form never does (its root is `#app-embed`). The hide/lift
  // rules in shopify-forms-theme.liquid are scoped to this attribute so they
  // affect only the floating teaser — an inline form (e.g. one placed in or near
  // the footer) is never hidden by the footer/drawer rules. Re-evaluated on every
  // shadow mutation because the teaser can hydrate after the host is wired.
  function markFloating(el) {
    if (!el.shadowRoot) return;
    if (el.shadowRoot.querySelector('.appEmbed')) {
      el.setAttribute('data-forms-floating', '');
    } else {
      el.removeAttribute('data-forms-floating');
    }
  }

  function wire(el) {
    if (el.__formsThemeWired || !el.shadowRoot) return;
    el.__formsThemeWired = true;
    inject(el.shadowRoot);
    markFloating(el);
    // The app re-renders on step changes; re-inject if our <style> is dropped
    // and re-check whether this host is the floating teaser.
    new MutationObserver(function () {
      inject(el.shadowRoot);
      markFloating(el);
    }).observe(el.shadowRoot, { childList: true });
  }

  function scan() {
    var els = document.querySelectorAll('shopify-forms-embed');
    for (var i = 0; i < els.length; i++) wire(els[i]);
    return els.length > 0 && Array.prototype.every.call(els, function (e) {
      return e.__formsThemeWired;
    });
  }

  // The embed hydrates (attaches its shadow root) asynchronously; poll briefly.
  var tries = 0;
  (function poll() {
    if (scan() || tries++ > 40) return;
    setTimeout(poll, 250);
  })();

  // Retract the app teaser/embed once the footer enters the viewport so it never
  // covers the footer links. Toggles [data-footer-in-view] on <body>; the CSS in
  // shopify-forms-theme.liquid hides <shopify-forms-embed> while it's set. The
  // observer fires the moment any part of the footer crosses the bottom edge
  // (threshold 0, viewport root), which is exactly "footer enters the bottom".
  (function watchFooter() {
    var footers = document.querySelectorAll('.shopify-section-group-footer-group');
    if (!footers.length || !('IntersectionObserver' in window)) return;
    var visible = new Set();
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) visible.add(entries[i].target);
        else visible.delete(entries[i].target);
      }
      if (visible.size > 0) document.body.setAttribute('data-footer-in-view', '');
      else document.body.removeAttribute('data-footer-in-view');
    });
    for (var j = 0; j < footers.length; j++) io.observe(footers[j]);
  })();
})();
